/**
 * OfflineContext.js
 * Context central pour la gestion offline d'EasyFacture
 *
 * Fournit :
 * - isOnline : état de la connexion réseau
 * - isOfflineReady : données locales disponibles
 * - syncStatus : idle | syncing | success | error
 * - subscription : état de l'abonnement offline
 * - triggerSync() : lancer une sync manuelle
 * - pendingCount : nb d'opérations en attente de sync
 */

import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { openDB, metaGet, getPendingQueue, getLocalStats } from '../db/localDB';
import { checkSubscriptionOffline, getOfflineStatus } from '../services/subscriptionService';
import { fullSync, quickSync, getLastSyncInfo } from '../services/syncService';
import toast from 'react-hot-toast';

const OfflineContext = createContext(null);

export function OfflineProvider({ children }) {
  const [isOnline, setIsOnline]             = useState(navigator.onLine);
  const [isOfflineReady, setIsOfflineReady] = useState(false);
  const [syncStatus, setSyncStatus]         = useState('idle'); // idle | syncing | success | error
  const [syncProgress, setSyncProgress]     = useState(null);
  const [pendingCount, setPendingCount]     = useState(0);
  const [subscription, setSubscription]     = useState(null);
  const [lastSync, setLastSync]             = useState(null);
  const [localStats, setLocalStats]         = useState(null);
  const syncInProgress = useRef(false);

  // ── Init DB au démarrage ──────────────────────────────────────────────
  useEffect(() => {
    openDB()
      .then(() => setIsOfflineReady(true))
      .catch(err => console.error('[Offline] DB init failed:', err));
  }, []);

  // ── Écouter les changements réseau ────────────────────────────────────
  useEffect(() => {
    const handleOnline  = () => { setIsOnline(true);  };
    const handleOffline = () => { setIsOnline(false); };

    window.addEventListener('online',  handleOnline);
    window.addEventListener('offline', handleOffline);
    return () => {
      window.removeEventListener('online',  handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // ── Quand on revient online → sync auto ────────────────────────────────
  useEffect(() => {
    if (isOnline && isOfflineReady) {
      refreshPendingCount();
      // Sync auto légère si items en attente
      getPendingQueue().then(queue => {
        if (queue.length > 0) {
          triggerSync('quick');
        }
      });
    }
  }, [isOnline, isOfflineReady]);

  // ── Vérifier abonnement au démarrage ──────────────────────────────────
  useEffect(() => {
    if (!isOfflineReady) return;
    refreshSubscription();
    refreshLastSync();
    refreshLocalStats();
  }, [isOfflineReady]);

  // ── Rafraîchir les données toutes les 30s ──────────────────────────────
  useEffect(() => {
    const interval = setInterval(() => {
      refreshPendingCount();
      refreshSubscription();
    }, 30000);
    return () => clearInterval(interval);
  }, [isOfflineReady]);

  // ── Helpers ───────────────────────────────────────────────────────────
  const refreshPendingCount = useCallback(async () => {
    const queue = await getPendingQueue();
    setPendingCount(queue.length);
  }, []);

  const refreshSubscription = useCallback(async () => {
    const status = await getOfflineStatus();
    setSubscription(status);
  }, []);

  const refreshLastSync = useCallback(async () => {
    const { lastSync: ls } = await getLastSyncInfo();
    setLastSync(ls);
  }, []);

  const refreshLocalStats = useCallback(async () => {
    const stats = await getLocalStats();
    setLocalStats(stats);
  }, []);

  // ── Déclencher une synchronisation ────────────────────────────────────
  const triggerSync = useCallback(async (mode = 'full') => {
    if (syncInProgress.current) return;
    if (!isOnline) {
      toast.error('Pas de connexion internet');
      return;
    }

    syncInProgress.current = true;
    setSyncStatus('syncing');
    setSyncProgress({ phase: 'start', message: 'Synchronisation en cours…' });

    try {
      const onProgress = (progress) => setSyncProgress(progress);

      let result;
      if (mode === 'quick') {
        result = await quickSync(onProgress);
        if (result.uploaded > 0) {
          toast.success(`${result.uploaded} élément${result.uploaded > 1 ? 's' : ''} synchronisé${result.uploaded > 1 ? 's' : ''} ✓`);
        }
      } else {
        result = await fullSync(onProgress);
        toast.success('Synchronisation complète ✓');
      }

      setSyncStatus('success');
      await refreshPendingCount();
      await refreshSubscription();
      await refreshLastSync();
      await refreshLocalStats();

      return result;
    } catch (err) {
      setSyncStatus('error');
      toast.error('Erreur de synchronisation');
      console.error('[Sync] Error:', err);
    } finally {
      syncInProgress.current = false;
      setSyncProgress(null);
      // Reset status après 3s
      setTimeout(() => setSyncStatus('idle'), 3000);
    }
  }, [isOnline, refreshPendingCount, refreshSubscription, refreshLastSync, refreshLocalStats]);

  const value = {
    isOnline,
    isOfflineReady,
    syncStatus,
    syncProgress,
    pendingCount,
    subscription,
    lastSync,
    localStats,
    triggerSync,
    refreshPendingCount,
    refreshLocalStats,
  };

  return (
    <OfflineContext.Provider value={value}>
      {children}
    </OfflineContext.Provider>
  );
}

export function useOffline() {
  const ctx = useContext(OfflineContext);
  if (!ctx) throw new Error('useOffline must be used within OfflineProvider');
  return ctx;
}